import { useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { notification } from "antd";
import { useLocation, useNavigate } from "react-router-dom";

// Avisos de Amigos en cualquier pantalla: reto recibido, reto aceptado, resultados y monedas
// de Invita y gana. Llegan por socket (FRIEND_NOTICE) y, si el jugador no estaba conectado,
// se muestran al entrar (GET /friends/challenges/pending-count) y se marcan como vistos.
const CHALLENGE_KINDS = new Set(["challenge_received"]);

const FriendNotifier = ({ language = "es", onChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const shownRef = useRef(new Set());
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const es = language === "es";

  const show = useCallback(
    (notice) => {
      if (!notice?.id || shownRef.current.has(notice.id)) return;
      shownRef.current.add(notice.id);
      const isChallenge = CHALLENGE_KINDS.has(notice.kind);
      const coins = Number(notice.coins) || 0;
      const key = `friend-notice-${notice.id}`;
      const open = () => {
        notification.destroy(key);
        navigate(`/game/friends?tab=${isChallenge || notice.challengeId ? "challenges" : "invite"}`);
      };
      notification.open({
        key,
        duration: isChallenge ? 12 : 8,
        message: isChallenge ? (es ? "⚔ Nuevo reto" : "⚔ New challenge") : coins ? `🪙 +${coins}` : es ? "👥 Amigos" : "👥 Friends",
        description: notice.text,
        btn:
          pathRef.current === "/game/friends" ? null : (
            <button type="button" className="ant-btn ant-btn-primary ant-btn-sm" onClick={open}>
              {isChallenge ? (es ? "Ver reto" : "View") : es ? "Ver" : "View"}
            </button>
          ),
      });
      axios.post("/friends/notices/seen", { ids: [notice.id] }).catch(() => {});
      if (typeof onChange === "function") onChange();
    },
    [es, navigate, onChange]
  );

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/friends/challenges/pending-count")
      .then((response) => {
        if (cancelled) return;
        (response?.data?.notices || []).slice(0, 4).reverse().forEach(show);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    let socket = null;
    const onNotice = (payload) => show(payload);
    const onStart = (payload) => {
      const tournamentId = String(payload?.tournamentId || "");
      if (!tournamentId || pathRef.current === "/game/friends") return;
      const key = `friend-start-${payload?.challengeId || tournamentId}`;
      notification.open({
        key,
        duration: 0,
        message: es ? "¡Reto aceptado!" : "Challenge accepted!",
        description: es
          ? `${payload?.fromName || "Tu amigo"} te espera en la partida.`
          : `${payload?.fromName || "Your friend"} is waiting in the game.`,
        btn: (
          <button
            type="button"
            className="ant-btn ant-btn-primary ant-btn-sm"
            onClick={() => {
              notification.destroy(key);
              navigate(`/game/${tournamentId}`);
            }}
          >
            {es ? "Ir a la partida" : "Go to the game"}
          </button>
        ),
      });
    };
    const attach = () => {
      const candidate = window.__ximaSocket || window.socket || null;
      if (!candidate || candidate === socket || typeof candidate.on !== "function") return;
      if (socket) {
        socket.off?.("FRIEND_NOTICE", onNotice);
        socket.off?.("FRIEND_CHALLENGE_START", onStart);
      }
      socket = candidate;
      socket.on("FRIEND_NOTICE", onNotice);
      socket.on("FRIEND_CHALLENGE_START", onStart);
    };
    attach();
    const timer = window.setInterval(attach, 1500);
    return () => {
      window.clearInterval(timer);
      if (socket) {
        socket.off?.("FRIEND_NOTICE", onNotice);
        socket.off?.("FRIEND_CHALLENGE_START", onStart);
      }
    };
  }, [es, navigate, show]);

  return null;
};

export default FriendNotifier;
